/**
 * Liquid Glass runtime: a TypeScript port of the glass lab's material.js
 * (final, rev. 2, without the lab shims). Framework-free and idempotent, with
 * no per-frame or per-scroll work.
 *
 * 1. Bars, in every engine. A top-level .glass-pill lens host with a fixed or
 *    sticky ancestor gets [data-lg-bar]. CSS gives bars a clear rim band and
 *    a calm core. Buttons and coins are never bars. A full-width .glass bar
 *    can carry data-lg-bar in markup instead, and the runtime never touches
 *    that one. The tag is re-checked when a class, or an inline position,
 *    changes on a bar candidate or one of its ancestors, so a header that
 *    turns sticky or fixed later becomes a bar (and stops being one when it
 *    is unpinned again).
 *
 * 2. Refraction, in Chromium only (see canRefract()). <html> gets .lg-svg (the
 *    root layout's GLASS_GATE_SCRIPT sets it before paint), and every eligible
 *    .glass-lens gets one displacement filter for its size. Eligible means:
 *    - a .glass-pill or .btn-glass-ghost host, or a coin-sized .glass
 *    - not inside other glass, a .glass-flat (tilt) host or a preserve-3d
 *      ancestor
 *    - at most LIMIT hosts. Hosts on or near the screen get the slots first
 *      (an on-screen host takes the slot of one that is off screen), and a
 *      freed slot goes to the hosts that were waiting for one.
 *    The map (see lensField()) is a rounded-rect SDF with a round bevel
 *    refracted at n = 1.5, plus a mild convex dome over the whole surface,
 *    so the backdrop shifts across the full capsule and not only at the rim.
 *    It is built on a CPU canvas after fonts load, cached per 4px size bucket
 *    and reference-counted. The centre frost is read from the host's own CSS
 *    frost token (--lg-blur-bar on bars, --lg-blur-lens on the rest), so the
 *    CSS glass and the lens match. An element switches to url() (by
 *    [data-lg] and --lg-url) only after its map has decoded, so a CSP that
 *    blocks data: images leaves plain CSS glass.
 *
 * DOM changes are watched with one debounced MutationObserver that only
 * rescans when a .glass-lens element or the #lg-defs host was added or
 * removed; text and unrelated attribute churn (counters, players) costs
 * nothing. A second observer listens to class and style changes on bar
 * candidates and their ancestors only, and reads styles only when a class or
 * an inline position changed (never for transforms, opacity or --lg-url).
 *
 * Importing this module has no side effects, because the server root layout
 * imports GLASS_GATE_SCRIPT from it. All DOM work starts in startLiquidGlass(),
 * which GlassRuntime calls once; it fills the <svg id="lg-defs"> host that
 * GlassDefs renders.
 */

interface Brand {
  brand: string;
  version: string;
}

type GlassNavigator = Navigator & {
  deviceMemory?: number;
  userAgentData?: { brands?: Brand[] };
};

interface Filter {
  id: string;
  el: SVGFilterElement;
  n: number;
  ready: Promise<void>;
}

/** Counters the runtime keeps on window.__lgMaterial (read by the lab tools). */
export interface LiquidGlassStats {
  maps: number;
  mapMs: number;
  filters: number;
  lenses: number;
  scans: number;
}

declare global {
  interface Window {
    __lgMaterial?: LiquidGlassStats;
  }
}

const LENS = "glass-lens";
const HOST = /(^|\s)(glass-pill|btn-glass-ghost|glass)(\s|$)/;
const GLASS_ANCESTOR =
  ".glass,.glass-pill,.glass-strong,.glass-flat,.btn-glass-ghost,.glass-tilt";
const LIMIT = 8;
const NS = "http://www.w3.org/2000/svg";
const MIN_CHROMIUM = 129;
const PREFS =
  "(prefers-reduced-transparency: reduce), (prefers-contrast: more)";
// the value of an inline `position` declaration (not background-position)
const INLINE_POSITION = /(?:^|[;\s])position\s*:\s*([^;]*)/;
const inlinePosition = (style: string | null): string =>
  INLINE_POSITION.exec(style || "")?.[1].trim() ?? "";

/* Lens shape. The bevel is BEVEL x the short side, 8-24px (20px on bars, so
   their label row keeps a calm frosted core): 22px on the 56px URL field.
   Its push peaks at PUSH x b near the rim and still carries about half of
   that at b/2. The rim band (map B) is clear glass at full weight out to
   CLEAR x b, then fades into the frost by b. DOME is the convex lens of the
   whole surface: every point samples DOME x its distance from the medial
   axis further in, a 1.18x magnification across the short side. */
const BEVEL = 0.4;
const BEVEL_MIN = 8;
const BEVEL_MAX = 24;
const BEVEL_BAR = 20;
const PUSH = 0.6;
const CLEAR = 0.5;
const DOME = 0.15;

/** Chromium >= 129 with backdrop-filter: url(), enough memory, no WebKit alias. */
export function engineCanRefract(): boolean {
  const nav = navigator as GlassNavigator;
  const brands = nav.userAgentData?.brands;
  return (
    !!brands?.some(
      b => b.brand === "Chromium" && parseInt(b.version, 10) >= MIN_CHROMIUM
    ) &&
    typeof CSS !== "undefined" &&
    typeof CSS.supports === "function" &&
    CSS.supports("backdrop-filter", "url(#a)") &&
    !CSS.supports("-webkit-backdrop-filter", "none") &&
    !(typeof nav.deviceMemory === "number" && nav.deviceMemory < 4) &&
    typeof ResizeObserver !== "undefined"
  );
}

/** The lens gate: engine support, no transparency / contrast preference, html[data-lg-solid] absent. */
export function canRefract(): boolean {
  return (
    engineCanRefract() &&
    !window.matchMedia(PREFS).matches &&
    !document.documentElement.hasAttribute("data-lg-solid")
  );
}

/**
 * Inline <head> script for the root layout: sets the lens gate (html.lg-svg)
 * before first paint. It mirrors canRefract() exactly; a test evaluates both
 * under the same environments. It skips the gate when html already has
 * data-lg-solid, and never sets that attribute itself. iOS browsers are all
 * WebKit and have no userAgentData, so they never pass. html.lg-svg alone
 * changes nothing visible: url() applies only to elements that also carry the
 * runtime's [data-lg], set after their map has decoded.
 */
export const GLASS_GATE_SCRIPT = `try{var d=document.documentElement;var n=navigator,u=n.userAgentData;if(!d.hasAttribute("data-lg-solid")&&u&&u.brands&&u.brands.some(function(b){return b.brand==="Chromium"&&parseInt(b.version,10)>=${MIN_CHROMIUM}})&&window.CSS&&CSS.supports("backdrop-filter","url(#a)")&&!CSS.supports("-webkit-backdrop-filter","none")&&!(n.deviceMemory<4)&&window.ResizeObserver&&!matchMedia(${JSON.stringify(PREFS)}).matches)d.classList.add("lg-svg")}catch(e){}`;

/* Bevel profile: displacement along the inward normal. t = distance from the
   edge / bevel width. The surface height is the round bevel
   h = sqrt(1 - (1 - t)^2). A vertical ray refracts at n = 1.5 and crosses
   h + .3 of glass. The push peaks just inside the rim, still carries about
   half of its peak at t = .5 and falls to 0 at t = 1; normalised to a peak
   of 1. Built on first use, never at import. */
let bevelLut: Float32Array | null = null;
function bevelProfile(): Float32Array {
  if (bevelLut) return bevelLut;
  const a = new Float32Array(256);
  let max = 0;
  for (let k = 0; k < 256; k++) {
    const u = 1 - k / 255;
    const u2 = u * u;
    const slope = u2 >= 1 ? 1e6 : u / Math.sqrt(1 - u2);
    const th = Math.atan(slope);
    const tr = Math.asin(Math.sin(th) / 1.5);
    a[k] = Math.tan(th - tr) * (Math.sqrt(1 - u2) + 0.3);
    if (a[k] > max) max = a[k];
  }
  for (let k = 0; k < 256; k++) a[k] /= max;
  return (bevelLut = a);
}

/** The bevel width (px) the lens uses for a host of this size. */
export function bevelWidth(W: number, H: number, bar = false): number {
  const cap = bar ? BEVEL_BAR : BEVEL_MAX;
  return Math.max(BEVEL_MIN, Math.min(cap, BEVEL * Math.min(W, H)));
}

/** A lens displacement map: RGBA pixels, its size and the filter's scale. */
export interface LensField {
  /** RGBA, MW x MH: R / G the push (128 = none), B the clear rim band, A 255 */
  data: Uint8ClampedArray;
  width: number;
  height: number;
  /** the bevel width in px */
  bevel: number;
  /** the largest push along one axis, in px (the map's full scale) */
  reach: number;
}

/**
 * The displacement map of a W x H rounded rect (radius R) at scale s (1 or
 * .5; feImage stretches it). R and G are the inward push (128 = none):
 * the round bevel within `bevel` px of the edge, plus the convex dome, which
 * pushes every point DOME x its distance from the medial axis (the centre
 * line of a capsule, the centre of a coin) towards that axis. Both fields
 * are continuous and zero on the axis, so the image never tears. B is the
 * clear rim band: 1 out to CLEAR x bevel, then a smooth fall-off to 0 at the
 * bevel's inner edge. Sizes are even, so one quadrant is computed and
 * mirrored. Pure: no DOM.
 */
export function lensField(
  W: number,
  H: number,
  R: number,
  bar = false,
  s = 1
): LensField {
  const lut = bevelProfile();
  const b = bevelWidth(W, H, bar);
  const MW = Math.round((W * s) / 2) * 2;
  const MH = Math.round((H * s) / 2) * 2;
  const p = new Uint8ClampedArray(MW * MH * 4);
  const hw = W / 2;
  const hh = H / 2;
  const r = Math.min(R, hw, hh);
  const ex = hw - r;
  const ey = hh - r;
  // the medial axis: a centre segment of half-length sx (or sy)
  const sx = Math.max(0, hw - hh);
  const sy = Math.max(0, hh - hw);
  const peak = PUSH * b;
  const reach = peak + DOME * Math.min(hw, hh);
  const clear = CLEAR * b;
  const kx = W / MW;
  const ky = H / MH;
  const row = MW * 4;
  for (let y = 0; y < MH / 2; y++) {
    const cy = hh - (y + 0.5) * ky; // distance from the centre, top half
    const qy = cy - ey;
    for (let x = 0; x < MW / 2; x++) {
      const cx = hw - (x + 0.5) * kx; // left half
      const qx = cx - ex;
      let dist: number; // distance inside the edge
      let ix: number; // inward normal (towards the centre: +x, +y here)
      let iy: number;
      if (qx > 0 && qy > 0) {
        const L = Math.sqrt(qx * qx + qy * qy);
        dist = r - L;
        ix = qx / L;
        iy = qy / L;
      } else if (qx > qy) {
        dist = r - qx;
        ix = 1;
        iy = 0;
      } else {
        dist = r - qy;
        ix = 0;
        iy = 1;
      }
      let m = 0;
      let B8 = 0;
      if (dist < b) {
        m = peak * lut[dist <= 0 ? 0 : ((dist / b) * 255) | 0];
        const u = dist <= clear ? 0 : (dist - clear) / (b - clear);
        B8 = 255 * (1 - u * u * (3 - 2 * u));
      }
      const vx = m * ix + DOME * Math.max(0, cx - sx);
      const vy = m * iy + DOME * Math.max(0, cy - sy);
      const R8 = Math.min(255, Math.round(128 + (127 * vx) / reach));
      const G8 = Math.min(255, Math.round(128 + (127 * vy) / reach));
      // write the pixel and its three mirrors (push flips sign with the axis)
      const i0 = y * row + x * 4;
      const i1 = y * row + (MW - 1 - x) * 4;
      const i2 = (MH - 1 - y) * row + x * 4;
      const i3 = (MH - 1 - y) * row + (MW - 1 - x) * 4;
      p[i0] = R8;
      p[i0 + 1] = G8;
      p[i1] = 256 - R8;
      p[i1 + 1] = G8;
      p[i2] = R8;
      p[i2 + 1] = 256 - G8;
      p[i3] = 256 - R8;
      p[i3 + 1] = 256 - G8;
      p[i0 + 2] = p[i1 + 2] = p[i2 + 2] = p[i3 + 2] = B8;
      p[i0 + 3] = p[i1 + 3] = p[i2 + 3] = p[i3 + 3] = 255;
    }
  }
  return { data: p, width: MW, height: MH, bevel: b, reach };
}

/** Starts the runtime once per page (bars everywhere, the lens where canRefract() passes). A no-op on the server. */
export function startLiquidGlass(): void {
  if (typeof window === "undefined" || window.__lgMaterial) return;
  const api: LiquidGlassStats = (window.__lgMaterial = {
    maps: 0,
    mapMs: 0,
    filters: 0,
    lenses: 0,
    scans: 0,
  });
  const d = document;
  const html = d.documentElement;
  const engineOk = engineCanRefract();
  const mq = window.matchMedia(PREFS);
  const allowed = (): boolean =>
    engineOk && !mq.matches && !html.hasAttribute("data-lg-solid");
  const lensy = (): boolean => html.classList.contains("lg-svg");

  const live = new Set<HTMLElement>();
  const pending = new Set<HTMLElement>();
  // the global setTimeout, as in material.js, so a late observer callback
  // needs no window binding
  let timer: ReturnType<typeof setTimeout> | null = null;
  const queue = (el: HTMLElement): void => {
    pending.add(el);
    if (!timer) timer = setTimeout(flush, 150);
  };

  /* ---- 1. bars (all engines) ---- */
  // A bar is a top-level .glass-pill lens host that is fixed / sticky or sits
  // in a fixed / sticky wrapper (HomeNavbar's pill is a static div in a fixed
  // <nav>). The runtime tracks the bars it tagged; a data-lg-bar that came
  // with the markup is never re-checked or removed.
  const tagged = new WeakSet<HTMLElement>();
  const candidate = (el: HTMLElement): boolean =>
    el.classList.contains("glass-pill") &&
    (tagged.has(el) || !el.hasAttribute("data-lg-bar")) &&
    !el.parentElement?.closest(GLASS_ANCESTOR);
  const pinned = (el: HTMLElement): boolean => {
    for (
      let p: HTMLElement | null = el;
      p && p !== d.body;
      p = p.parentElement
    ) {
      const pos = getComputedStyle(p).position;
      if (pos === "fixed" || pos === "sticky") return true;
    }
    return false;
  };
  const tagBar = (el: HTMLElement): void => {
    if (!el.isConnected || !candidate(el)) return;
    const bar = pinned(el);
    if (bar === tagged.has(el)) return;
    if (bar) {
      el.setAttribute("data-lg-bar", "");
      tagged.add(el);
    } else {
      el.removeAttribute("data-lg-bar");
      tagged.delete(el);
    }
    // bars take the bar frost and bevel: rebuild the lens
    if (lensy()) queue(el);
  };

  // Re-checks after a class change, or a change of the inline position, on
  // a candidate or an ancestor: debounced, and only the candidates read
  // styles. Transform, opacity or --lg-url churn never gets this far.
  let barTimer: ReturnType<typeof setTimeout> | null = null;
  const recheckBars = (): void => {
    barTimer = null;
    live.forEach(tagBar);
  };
  const barWatch = new MutationObserver(records => {
    if (barTimer) return;
    for (const r of records) {
      if (
        r.attributeName === "style" &&
        inlinePosition(r.oldValue) ===
          inlinePosition((r.target as Element).getAttribute("style"))
      )
        continue;
      barTimer = setTimeout(recheckBars, 200);
      return;
    }
  });
  // Observes only the bar candidates and their ancestors (no subtree), so a
  // tilt card or a counter elsewhere never wakes this observer up.
  const watchBars = (): void => {
    barWatch.disconnect();
    live.forEach(el => {
      if (!candidate(el)) return;
      for (let p: Element | null = el; p && p !== html; p = p.parentElement)
        barWatch.observe(p, {
          attributes: true,
          attributeFilter: ["class", "style"],
          attributeOldValue: true,
        });
    });
  };

  /* ---- 2. refraction (Chromium only) ---- */
  // The field is smooth and feImage stretches it, so large hosts (bars) are
  // mapped at half resolution.
  const makeMap = (W: number, H: number, R: number, bar: boolean) => {
    const t0 = performance.now();
    const f = lensField(W, H, R, bar, W * H > 30000 ? 0.5 : 1);
    const cv = d.createElement("canvas");
    cv.width = f.width;
    cv.height = f.height;
    // CPU canvas: no GPU readback stall
    const cx = cv.getContext("2d", { willReadFrequently: true });
    if (!cx) throw new Error("Liquid Glass: no 2d canvas context");
    const im = cx.createImageData(f.width, f.height);
    im.data.set(f.data);
    cx.putImageData(im, 0, 0);
    const url = cv.toDataURL("image/png");
    api.maps++;
    api.mapMs += performance.now() - t0;
    return { url, reach: f.reach };
  };

  const node = <K extends keyof SVGElementTagNameMap>(
    tag: K,
    attrs: Record<string, string>,
    parent?: Element
  ): SVGElementTagNameMap[K] => {
    const n = d.createElementNS(NS, tag);
    for (const k in attrs) n.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(n);
    return n;
  };

  let defs: SVGDefsElement | null = null;
  const cache: Record<string, Filter> = {};
  // the filter id each lens host currently uses (material.js keeps it on el.__lg)
  const lensOf = new WeakMap<HTMLElement, string | null>();

  const ensureDefs = (): SVGDefsElement => {
    if (defs && defs.isConnected) return defs;
    let svg: Element | null = d.getElementById("lg-defs");
    if (!svg) {
      const host = node("svg", {
        id: "lg-defs",
        width: "0",
        height: "0",
        "aria-hidden": "true",
        focusable: "false",
      });
      host.style.cssText =
        "position:absolute;width:0;height:0;overflow:hidden;pointer-events:none";
      d.body.appendChild(host);
      svg = host;
    }
    defs = svg.querySelector("defs") ?? node("defs", {}, svg);
    return defs;
  };

  /* One <filter> per size bucket, with 6 primitives. The input is the backdrop
     after a light CSS blur:
       bent = the backdrop displaced by the map (bevel + dome)
       soft = bent with `frost` px more blur (the frosted centre, which
              carries the dome's magnification too)
       out  = bent through the rim-band mask (map B), over soft
     The specular rim is a CSS box-shadow painted over the result, and the
     labels paint above the glass, so they never distort. */
  const filterFor = (
    W: number,
    H: number,
    R: number,
    frost: number,
    bar: boolean
  ): Filter => {
    const id = `lg-${W}-${H}-${R}-${frost}${bar ? "-bar" : ""}`;
    if (cache[id]) return cache[id];
    const { url, reach } = makeMap(W, H, R, bar);
    const f = node(
      "filter",
      {
        id,
        x: "0",
        y: "0",
        width: "1",
        height: "1",
        primitiveUnits: "objectBoundingBox",
        "color-interpolation-filters": "sRGB",
      },
      ensureDefs()
    );
    node(
      "feImage",
      {
        href: url,
        x: "0",
        y: "0",
        width: "1",
        height: "1",
        preserveAspectRatio: "none",
        result: "map",
      },
      f
    );
    // objectBoundingBox: Chromium scales both axes by the width, so the
    // largest push is `reach` px
    node(
      "feDisplacementMap",
      {
        in: "SourceGraphic",
        in2: "map",
        scale: ((2 * reach) / W).toFixed(5),
        xChannelSelector: "R",
        yChannelSelector: "G",
        result: "bent",
      },
      f
    );
    node(
      "feGaussianBlur",
      {
        in: "bent",
        stdDeviation: `${(frost / W).toFixed(5)} ${(frost / H).toFixed(5)}`,
        result: "soft",
      },
      f
    );
    node(
      "feColorMatrix",
      {
        in: "map",
        type: "matrix",
        values: "0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0 0",
        result: "band",
      },
      f
    );
    node(
      "feComposite",
      { in: "bent", in2: "band", operator: "in", result: "edge" },
      f
    );
    node("feComposite", { in: "edge", in2: "soft", operator: "over" }, f);
    const img = new Image();
    img.src = url;
    api.filters++;
    return (cache[id] = {
      id,
      el: f,
      n: 0,
      ready: img.decode ? img.decode() : Promise.resolve(),
    });
  };

  const release = (id: string | null | undefined): void => {
    const c = id ? cache[id] : undefined;
    if (c && --c.n <= 0) {
      c.el.remove();
      delete cache[c.id];
      api.filters--;
    }
  };

  /* LIMIT slots. Hosts on or near the screen (IntersectionObserver) come
     first; without the observer every host counts as near, so the slots go
     in DOM order. Hosts that found every slot taken wait for one. */
  const near = new Set<HTMLElement>();
  const waiting = new Set<HTMLElement>();
  let io: IntersectionObserver | null = null;
  const isNear = (el: HTMLElement): boolean => !io || near.has(el);

  // takes the lens off one host; true when it had one (a slot is free)
  const unlens = (el: HTMLElement): boolean => {
    const id = lensOf.get(el);
    if (!id) return false;
    release(id);
    lensOf.set(el, null);
    el.removeAttribute("data-lg");
    el.style.removeProperty("--lg-url");
    return true;
  };
  // a freed slot goes to the hosts waiting for one (near ones first)
  const refill = (): void => {
    if (lensy()) waiting.forEach(queue);
  };
  const drop = (el: HTMLElement): void => {
    waiting.delete(el);
    if (unlens(el)) refill();
  };

  const eligible = (el: HTMLElement): boolean => {
    if (!el.isConnected || !HOST.test(el.className)) return false;
    let p = el.parentElement;
    if (p?.closest(GLASS_ANCESTOR)) return false; // glass on glass
    for (; p && p !== d.body; p = p.parentElement) {
      // tilt host
      if (getComputedStyle(p).transformStyle === "preserve-3d") return false;
    }
    return true;
  };

  const lensCount = (): number => {
    let n = 0;
    live.forEach(el => {
      if (lensOf.get(el)) n++;
    });
    return n;
  };

  const apply = (el: HTMLElement): void => {
    if (!lensy() || !eligible(el)) return drop(el);
    const W = el.offsetWidth; // layout size: ignores transforms
    const H = el.offsetHeight;
    if (!W || !H || H > 160 || W * H > 160000) return drop(el);
    // a .glass host refracts only at coin size; cards stay CSS glass
    if (
      !el.classList.contains("glass-pill") &&
      !el.classList.contains("btn-glass-ghost") &&
      W * H > 40000
    )
      return drop(el);
    let victim: HTMLElement | undefined;
    if (!lensOf.get(el) && lensCount() >= LIMIT) {
      // an on-screen host takes the slot of one that is off screen
      if (isNear(el))
        for (const o of live)
          if (lensOf.get(o) && !isNear(o)) {
            victim = o;
            break;
          }
      if (!victim) {
        waiting.add(el);
        return;
      }
    }
    waiting.delete(el);
    const cs = getComputedStyle(el);
    const rs = cs.borderTopLeftRadius;
    let R = parseFloat(rs) || 0;
    if (rs.indexOf("%") > 0) R = (R / 100) * Math.min(W, H);
    const kw = Math.max(8, Math.round(W / 4) * 4);
    const kh = Math.max(8, Math.round(H / 4) * 4);
    const bar = el.hasAttribute("data-lg-bar");
    // the lens frost is the host's CSS frost, so both paths look the same
    const frost = Math.round(
      parseFloat(
        cs.getPropertyValue(bar ? "--lg-blur-bar" : "--lg-blur-lens")
      ) || 8
    );
    const c = filterFor(
      kw,
      kh,
      Math.round(Math.min(R, kw / 2, kh / 2)),
      frost,
      bar
    );
    if (lensOf.get(el) === c.id) return;
    release(lensOf.get(el));
    c.n++;
    lensOf.set(el, c.id);
    // evicted after the new host took its filter, so a shared size bucket
    // is never rebuilt
    if (victim) {
      unlens(victim);
      waiting.add(victim);
    }
    c.ready.then(
      () => {
        if (lensOf.get(el) !== c.id || !c.el.isConnected) return;
        el.style.setProperty("--lg-url", `url(#${c.id})`);
        el.setAttribute("data-lg", "");
      },
      () => drop(el)
    );
  };

  let ro: ResizeObserver | null = null;
  // time-sliced: yields to the page every ~6ms, so map builds never form a
  // long task. Hosts near the screen go first.
  function flush(): void {
    timer = null;
    const list = Array.from(pending).sort(
      (a, b) => Number(isNear(b)) - Number(isNear(a))
    );
    pending.clear();
    const next = (): void => {
      const t0 = performance.now();
      while (list.length && performance.now() - t0 < 6)
        apply(list.shift() as HTMLElement);
      if (list.length) setTimeout(next, 0);
    };
    next();
  }

  const watch = (el: HTMLElement): boolean => {
    if (live.has(el)) return false;
    live.add(el);
    tagBar(el);
    ro?.observe(el);
    io?.observe(el);
    if (lensy()) queue(el);
    return true;
  };

  const lensEls = d.getElementsByClassName(LENS); // live collection
  const scan = (): void => {
    api.scans++;
    let changed = false;
    Array.prototype.forEach.call(lensEls, (el: HTMLElement) => {
      if (watch(el)) changed = true;
    });
    live.forEach(el => {
      if (el.isConnected) return;
      ro?.unobserve(el);
      io?.unobserve(el);
      near.delete(el);
      drop(el);
      live.delete(el);
      changed = true;
    });
    api.lenses = live.size;
    if (changed) watchBars();
  };

  /* A filter (or the whole defs host) was removed, e.g. by a React unmount:
     those hosts go back to plain CSS glass (full CSS frost, never the bare
     2px pre-blur), and a fresh filter is built for them. */
  const heal = (): void => {
    const dead = Object.keys(cache).filter(id => !cache[id].el.isConnected);
    if (!dead.length) return;
    dead.forEach(id => {
      delete cache[id];
      api.filters--;
    });
    if (defs && !defs.isConnected) defs = null;
    live.forEach(el => {
      if (dead.indexOf(lensOf.get(el) || "") < 0) return;
      lensOf.set(el, null);
      el.removeAttribute("data-lg");
      el.style.removeProperty("--lg-url");
      queue(el);
    });
  };

  const setOn = (on: boolean): void => {
    html.classList.toggle("lg-svg", on);
    if (!on) waiting.clear();
    live.forEach(on ? queue : drop);
  };

  // Does this mutation batch touch lens hosts or the filter host? Text,
  // attribute and unrelated subtree churn returns false without a scan.
  // nodeType 1 is Node.ELEMENT_NODE, compared literally as in material.js so
  // the callback needs no DOM global.
  const relevant = (records: MutationRecord[]): boolean => {
    for (const r of records) {
      if (
        defs &&
        r.target.nodeType === 1 &&
        defs.contains(r.target) &&
        r.removedNodes.length
      )
        return true;
      for (const list of [r.addedNodes, r.removedNodes]) {
        for (const n of Array.from(list)) {
          if (n.nodeType !== 1) continue;
          const el = n as Element;
          if (
            el.classList.contains(LENS) ||
            el.id === "lg-defs" ||
            el.getElementsByClassName(LENS).length ||
            el.querySelector("#lg-defs")
          )
            return true;
        }
      }
    }
    return false;
  };

  const start = (): void => {
    if (engineOk) {
      ro = new ResizeObserver(entries =>
        entries.forEach(en => queue(en.target as HTMLElement))
      );
      // fires only when a host crosses the (widened) viewport edge
      if (typeof IntersectionObserver !== "undefined")
        io = new IntersectionObserver(
          entries =>
            entries.forEach(en => {
              const el = en.target as HTMLElement;
              if (en.isIntersecting) {
                near.add(el);
                if (waiting.has(el)) queue(el);
              } else {
                near.delete(el);
              }
            }),
          { rootMargin: "50% 0px" }
        );
    }
    scan();
    // SPA navigation: pick up lenses that mount later and forget removed ones
    // (debounced, and only for batches that touch lenses or #lg-defs).
    let sched: ReturnType<typeof setTimeout> | null = null;
    new MutationObserver(records => {
      if (sched || !relevant(records)) return;
      sched = setTimeout(() => {
        sched = null;
        scan();
        heal();
      }, 200);
    }).observe(d.body, { childList: true, subtree: true });
    if (engineOk) {
      const go = (): void => setOn(allowed());
      mq.addEventListener?.("change", go);
      // html[data-lg-solid] turns the lens off and back on
      new MutationObserver(go).observe(html, {
        attributes: true,
        attributeFilter: ["data-lg-solid"],
      });
      // build maps only once fonts settled sizes (no throwaway pre-font maps)
      if (d.fonts?.ready) d.fonts.ready.then(go, go);
      else go();
    }
  };

  if (d.body) start();
  else d.addEventListener("DOMContentLoaded", start, { once: true });
}
